import { useEffect, useId, useRef, useState } from "react";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import { Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { discussionApi, investingApi } from "../../services/api";
import { apiErrorMessage, parseApiError } from "../../services/apiError";
import { revealLater } from "./lib/reveal";
import useSubmitGuard from "../../hooks/useSubmitGuard";
import { companyUrl, discussionBody, discussionTitle, openingPost } from "./lib/discussion";

const MAIN = "h-11 md:h-9";
// A post's limit (backend/discussions/services.py MAX_POST_CHARS).
const POST_MAX = 10_000;

/**
 * Discuss (spec walkthrough 6): starts a Mono discussion titled
 * "<SYMBOL> — own?" — the household's, people and agents — whose first post
 * ends with a link back to the company's page, then links it to the
 * company (PATCH discussion), and the page shows it.
 *
 * Two requests, each failure said as it is (the Research this pattern):
 * - the start refused: nothing was started; unanswered: it may have been,
 *   check Discussions before trying again;
 * - the link failed after the discussion was started: it stays, and the
 *   dialog offers to link THAT one again — never a second discussion.
 *   `onCreated(symbol, discussion)` hands it to the page the moment it
 *   exists; the section keeps it (useUnlinked: by symbol, for this tab),
 *   so the dialog reopens on "Link discussion #N" (`pending`).
 *
 * A refused post lands on the post; any other failure is brought into view
 * and focused (lib/reveal.js).
 *
 * `onLinked(symbol, page, discussion)` gets the company's page as the PATCH
 * returned it. Static backdrop; Escape and Cancel refused while it works.
 */
function DiscussDialog({
  open,
  company,
  pending = null,
  onOpenChange,
  onCreated,
  onLinked,
  onForget,
  onCloseAutoFocus,
}) {
  const id = useId();
  const symbol = company?.symbol ?? "";
  const origin = window.location.origin;
  const [text, setText] = useState("");
  const [textError, setTextError] = useState(null);
  const [busy, setBusy] = useState(null); // "create" | "link"
  const [error, setError] = useState(null); // {text, unknown?}
  const [made, setMade] = useState(pending);
  const textRef = useRef(null);
  const linkRef = useRef(null);
  const errorRef = useRef(null);
  const submitGuard = useSubmitGuard(open);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // Each opening starts from the page's view of this company — set while
  // rendering the opening, not in an effect: Radix picks the field to focus
  // in its own mount effect, which runs before ours.
  const opening = open ? symbol : null;
  const [openedFor, setOpenedFor] = useState(null);
  if (opening !== openedFor) {
    setOpenedFor(opening);
    if (opening !== null) {
      setMade(pending);
      setText(openingPost({ symbol, name: company?.name }));
      setTextError(null);
      setBusy(null);
      setError(null);
    }
  }

  const link = async (discussion) => {
    setBusy("link");
    setError(null);
    try {
      const { data } = await investingApi.companies.patch(symbol, { discussion: discussion.id });
      onLinked(symbol, data, discussion);
      if (mounted.current) onOpenChange(false);
    } catch (err) {
      if (!mounted.current) return;
      setError({
        text: `Discussion #${discussion.id} was started, but linking it to ${symbol} failed: ${apiErrorMessage(err, "Something went wrong.")}`,
      });
      setTimeout(() => linkRef.current?.focus(), 0);
    } finally {
      if (mounted.current) setBusy(null);
    }
  };

  const start = async (event) => {
    event.preventDefault();
    if (busy) return;
    if (!text.trim()) {
      setTextError("Write the opening post — it's what the discussion starts from.");
      textRef.current?.focus();
      return;
    }
    setBusy("create");
    setError(null);
    let discussion;
    try {
      const { data } = await discussionApi.create({
        title: discussionTitle(symbol),
        body: discussionBody({ symbol, text, origin }),
      });
      discussion = data;
    } catch (err) {
      if (!mounted.current) return;
      setBusy(null);
      // An answer means the server refused: nothing was started. No answer
      // (offline, a timeout) means it may have been.
      if (!err?.response) {
        setError({
          text: `No answer from the server (${apiErrorMessage(err, "network error")}), so the discussion may have been started.`,
          unknown: true,
        });
        revealLater(errorRef);
        return;
      }
      // The server's word on the post goes on the post; the rest above
      // the buttons, brought into view.
      const { formError, fieldErrors } = parseApiError(err, "Couldn't start the discussion.");
      const { body: postRefused, ...otherFields } = fieldErrors;
      const rest = [formError, ...Object.entries(otherFields).map(([name, value]) => `${name}: ${[].concat(value).join(", ")}`)]
        .filter(Boolean)
        .join(" — ");
      if (postRefused) setTextError(`${[].concat(postRefused).join(" ")} No discussion was started.`);
      if (rest) setError({ text: `${rest} No discussion was started — fix that and try again.` });
      if (postRefused) setTimeout(() => textRef.current?.focus(), 0);
      else revealLater(errorRef);
      return;
    }
    onCreated(symbol, discussion);
    if (!mounted.current) return;
    setMade(discussion);
    await link(discussion);
  };

  const forget = () => {
    onForget?.(symbol);
    setMade(null);
    setError(null);
    setTimeout(() => textRef.current?.focus(), 0);
  };

  const linking = Boolean(made);
  const url = companyUrl(symbol, origin);
  return (
    <Dialog open={open} onOpenChange={(next) => onOpenChange(next)}>
      <DialogContent
        data-investing=""
        showCloseButton={false}
        className="flex max-h-[85dvh] flex-col gap-0 p-0 sm:max-w-lg"
        onPointerDownOutside={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
        onEscapeKeyDown={(event) => busy && event.preventDefault()}
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          (pending ? linkRef : textRef).current?.focus();
        }}
        onCloseAutoFocus={onCloseAutoFocus}
      >
        <DialogHeader className="border-b px-6 py-4 text-left">
          <DialogTitle className="font-display text-xl">Discuss {symbol}</DialogTitle>
          <DialogDescription>
            Starts a discussion titled &ldquo;{discussionTitle(symbol)}&rdquo; for your household — people
            and their agents can read and reply — then links it here, where its latest post and any
            decision you pin show up. The verdict itself stays on this page.
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={linking ? (e) => { e.preventDefault(); if (!busy) link(made); } : start}
          noValidate
          className="flex min-h-0 flex-1 flex-col"
        >
          <div data-slot="dialog-body" className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-6 py-4">
            {linking ? (
              !error && (
                <p className="m-0">
                  Discussion #{made.id} was started but isn&apos;t linked to {symbol} yet. Linking it shows
                  it on {symbol}&apos;s page.
                </p>
              )
            ) : (
              <div className="flex flex-col gap-1.5">
                <Label htmlFor={`${id}-post`}>Opening post</Label>
                <Textarea
                  ref={textRef}
                  id={`${id}-post`}
                  value={text}
                  maxLength={POST_MAX - url.length - 20}
                  className="max-h-48 min-h-24"
                  disabled={Boolean(busy)}
                  aria-invalid={textError ? true : undefined}
                  aria-describedby={`${id}-post-help${textError ? ` ${id}-post-error` : ""}`}
                  onChange={(event) => {
                    setText(event.target.value);
                    setTextError(null);
                  }}
                />
                <p id={`${id}-post-help`} className="m-0 text-xs wrap-anywhere text-muted-foreground">
                  Markdown. The post ends with a link back to this page: {url}
                </p>
                {textError && (
                  <p id={`${id}-post-error`} className="m-0 text-sm text-destructive">
                    {textError}
                  </p>
                )}
              </div>
            )}
            {error && (
              <Alert ref={errorRef} variant="destructive" role="alert" tabIndex={-1} className="outline-none">
                <AlertDescription>
                  {error.text}
                  {error.unknown && (
                    <>
                      {" "}
                      Check <Link to="/discussions">Discussions</Link> for &ldquo;{discussionTitle(symbol)}
                      &rdquo; before trying again.
                    </>
                  )}
                </AlertDescription>
              </Alert>
            )}
            {linking && !busy && (
              <p className="m-0 text-xs text-muted-foreground">
                Discussion #{made.id} gone?{" "}
                <button type="button" className="text-primary underline underline-offset-2" onClick={forget}>
                  Start over with a new discussion
                </button>
              </p>
            )}
          </div>
          <DialogFooter className="border-t px-6 py-4">
            <Button type="button" variant="outline" className={MAIN} disabled={Boolean(busy)} onClick={() => onOpenChange(false)}>
              {linking ? "Close" : "Cancel"}
            </Button>
            <Button ref={linkRef} type="submit" className={MAIN} disabled={Boolean(busy)} {...submitGuard}>
              {busy && <Loader2 className="animate-spin" aria-hidden="true" />}
              {busy === "create"
                ? "Starting the discussion…"
                : busy === "link"
                  ? `Linking discussion #${made?.id}…`
                  : linking
                    ? error
                      ? `Link discussion #${made.id} again`
                      : `Link discussion #${made.id}`
                    : "Start the discussion and link it"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

DiscussDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  /** The company: `{symbol, name}`. */
  company: PropTypes.shape({ symbol: PropTypes.string.isRequired, name: PropTypes.string }),
  /** A discussion started earlier and not linked: link it, don't start another. */
  pending: PropTypes.shape({ id: PropTypes.number.isRequired }),
  onOpenChange: PropTypes.func.isRequired,
  /** `(symbol, discussion)`: it exists (before the link is tried). */
  onCreated: PropTypes.func.isRequired,
  /** `(symbol, page, discussion)`: linked; `page` is the PATCH's company page. */
  onLinked: PropTypes.func.isRequired,
  /** `(symbol)`: drop the unlinked discussion and start over. */
  onForget: PropTypes.func,
  /** Radix's close autofocus: the page moves focus when the opener went away. */
  onCloseAutoFocus: PropTypes.func,
};

export default DiscussDialog;
